import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, Check, Plus } from "@phosphor-icons/react";

/**
 * Lightweight searchable combobox with "Add new value" support.
 *
 * - Click → open dropdown of options (type to filter).
 * - Enter / "Add [value]" row → persist the typed string onto the product.
 *   Next time anyone opens the form, the freshly-saved value will come back
 *   from the backend distinct fetch and show up as a selectable option, so
 *   no Settings management is required.
 */
export default function ComboBox({
    value = "",
    options = [],
    onChange,
    placeholder = "Select…",
    allowCreate = true,
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) setQuery("");
    }, [open]);

    useEffect(() => {
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.toLowerCase().includes(q));
    }, [query, options]);

    const canCreate =
        allowCreate &&
        query.trim().length > 0 &&
        !options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

    const commit = (val) => {
        onChange?.(val);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (filtered.length > 0) commit(filtered[0]);
            else if (canCreate) commit(query.trim());
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm text-left flex items-center justify-between gap-2 hover:border-sage-dark transition-colors"
                data-testid={testId ? `${testId}-trigger` : undefined}
            >
                <span className={value ? "text-ink truncate" : "text-neutral-400 truncate"}>
                    {value || placeholder}
                </span>
                <CaretDown size={14} weight="light" className="text-neutral-400 shrink-0" />
            </button>
            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-mist rounded-sm shadow-lg">
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Search or type to add…"
                        className="w-full px-3 py-2 border-b border-mist outline-none text-base md:text-sm"
                        data-testid={testId ? `${testId}-search` : undefined}
                    />
                    <ul className="max-h-56 overflow-y-auto py-1">
                        {filtered.length === 0 && !canCreate && (
                            <li className="px-3 py-2 text-xs text-neutral-400">No matches</li>
                        )}
                        {filtered.map((o) => (
                            <li key={o}>
                                <button
                                    type="button"
                                    onClick={() => commit(o)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-sage-soft/40 flex items-center justify-between gap-2"
                                    data-testid={testId ? `${testId}-option-${o}` : undefined}
                                >
                                    <span className="truncate">{o}</span>
                                    {value === o && <Check size={14} weight="light" className="text-sage-dark" />}
                                </button>
                            </li>
                        ))}
                        {canCreate && (
                            <li>
                                <button
                                    type="button"
                                    onClick={() => commit(query.trim())}
                                    className="w-full text-left px-3 py-2 text-sm text-sage-dark hover:bg-sage-soft/40 inline-flex items-center gap-1.5"
                                    data-testid={testId ? `${testId}-create` : undefined}
                                >
                                    <Plus size={12} weight="bold" /> Add “{query.trim()}”
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
